import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HiCheck } from 'react-icons/hi';
import apiService from '../services/apiService';
import '../styles/portfolioMatrix.css';

// Componente de célula memoizado para evitar re-renders desnecessários
const MatrixCell = React.memo(({ rowId, colId, isChecked, isDiagonal, onToggle, isHighlightedCell }) => {
    return (
        <td
            className={`portfolio-cell ${isDiagonal ? 'diagonal' : ''} ${isHighlightedCell ? 'cell-highlighted' : ''}`}
            onClick={onToggle}
        >
            <div className={`checkbox-wrapper ${isChecked ? 'checked' : ''}`}>
                {isChecked && <HiCheck className="check-icon" />}
            </div>
        </td>
    );
});

MatrixCell.displayName = 'MatrixCell';

// Componente de header de coluna memoizado
const ColumnHeader = React.memo(({ id }) => {
    return (
        <th className="portfolio-header">
            <div className="portfolio-header-text">{id}</div>
        </th>
    );
});

ColumnHeader.displayName = 'ColumnHeader';

// Componente de header de linha memoizado
const RowHeader = React.memo(({ id, name, isSearchHighlighted }) => {
    return (
        <td
            className={`portfolio-row-header ${isSearchHighlighted ? 'search-highlighted' : ''}`}
            data-portfolio-id={id}
        >
            <div className="portfolio-row-header-content">
                <span className="portfolio-id">{id}</span>
                <span className="portfolio-name" title={name}>
                    {name?.substring(0, 25)}
                    {name?.length > 25 ? '...' : ''}
                </span>
            </div>
        </td>
    );
});

RowHeader.displayName = 'RowHeader';

const PortfolioMatrixModal = ({ show, onHide, portfolios, onSave }) => {
    // Matriz de seleção: matriz[i][j] indica se portfolio i está correlacionado com portfolio j
    const [selectionMatrix, setSelectionMatrix] = useState({});
    const [originalMatrix, setOriginalMatrix] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedPortfolio, setHighlightedPortfolio] = useState(null);
    const [cellSearchTerm, setCellSearchTerm] = useState('');
    const [highlightedCell, setHighlightedCell] = useState({ row: null, col: null });

    const searchInputRef = useRef(null);
    const cellSearchInputRef = useRef(null);

    const portfolioIds = useMemo(
        () => Object.keys(portfolios).map(id => parseInt(id)).sort((a, b) => a - b),
        [portfolios]
    );

    // Carregar matriz toda vez que o modal abrir
    useEffect(() => {
        if (show && portfolioIds.length > 0) {
            setIsLoading(true);

            // Carregar a matriz padrão do backend
            apiService.obterPortfoliosCorrelatos()
                .then(response => {
                    if (response.sucesso && response.portfoliosCorrelatos !== undefined) {
                        // Converter grupos em matriz de seleção
                        const initialMatrix = {};
                        portfolioIds.forEach(id => {
                            initialMatrix[id] = {};
                            portfolioIds.forEach(otherId => {
                                initialMatrix[id][otherId] = id === otherId;
                            });
                        });

                        // Marcar correlações dos grupos
                        if (Array.isArray(response.portfoliosCorrelatos)) {
                            response.portfoliosCorrelatos.forEach(grupo => {
                                if (Array.isArray(grupo)) {
                                    grupo.forEach(id1 => {
                                        grupo.forEach(id2 => {
                                            if (initialMatrix[id1] && initialMatrix[id1][id2] !== undefined) {
                                                initialMatrix[id1][id2] = true;
                                            }
                                        });
                                    });
                                }
                            });
                        }

                        setSelectionMatrix(initialMatrix);
                        setOriginalMatrix(JSON.parse(JSON.stringify(initialMatrix)));
                        setIsInitialized(true);
                    } else {
                        inicializarMatrizVazia();
                        setIsInitialized(true);
                    }
                })
                .catch(() => {
                    inicializarMatrizVazia();
                    setIsInitialized(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [show, portfolioIds]);

    const inicializarMatrizVazia = () => {
        const initialMatrix = {};
        portfolioIds.forEach(id => {
            initialMatrix[id] = {};
            portfolioIds.forEach(otherId => {
                initialMatrix[id][otherId] = id === otherId;
            });
        });
        setSelectionMatrix(initialMatrix);
        setOriginalMatrix(JSON.parse(JSON.stringify(initialMatrix))); // Deep copy
    };


    // Converter matriz de seleção em lista de todas as correlações
    const convertMatrixToGroups = useCallback((matrix) => {
        const correlacoes = [];

        // Para cada linha da matriz, coletar todos os portfólios correlacionados
        portfolioIds.forEach(portfolioId => {
            const correlacionados = [];

            portfolioIds.forEach(otherId => {
                // Incluir correlações (exceto diagonal)
                if (portfolioId !== otherId && matrix[portfolioId][otherId]) {
                    correlacionados.push(otherId);
                }
            });

            // Se há correlações para este portfólio, adicionar à lista
            if (correlacionados.length > 0) {
                correlacoes.push([portfolioId, ...correlacionados.sort((a, b) => a - b)]);
            }
        });

        return correlacoes;
    }, [portfolioIds]);

    const toggleSelection = useCallback((rowId, colId) => {
        if (rowId === colId) return; // Não permitir desmarcar a diagonal

        setSelectionMatrix(prev => {
            const newMatrix = { ...prev };
            // Toggle both directions (symmetric matrix)
            const newValue = !prev[rowId][colId];
            newMatrix[rowId] = { ...prev[rowId], [colId]: newValue };
            newMatrix[colId] = { ...prev[colId], [rowId]: newValue };

            return newMatrix;
        });
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const grupos = convertMatrixToGroups(selectionMatrix);
            await onSave(grupos);
            setOriginalMatrix(JSON.parse(JSON.stringify(selectionMatrix))); // Update original after save
            onHide();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar configuração');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setSelectionMatrix(JSON.parse(JSON.stringify(originalMatrix))); // Restore original
        setSearchTerm('');
        setCellSearchTerm('');
        setHighlightedPortfolio(null);
        setHighlightedCell({ row: null, col: null });
        onHide();
    };

    // Limpar todas as seleções (exceto diagonal)
    const handleClearAll = useCallback(() => {
        setSelectionMatrix(prev => {
            const newMatrix = {};
            portfolioIds.forEach(id => {
                newMatrix[id] = {};
                portfolioIds.forEach(otherId => {
                    // Manter apenas a diagonal
                    newMatrix[id][otherId] = id === otherId;
                });
            });

            return newMatrix;
        });
    }, [portfolioIds]);

    // Buscar portfólio automaticamente ao digitar (com debounce)
    useEffect(() => {
        const term = searchTerm.trim();

        if (!term) {
            setHighlightedPortfolio(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            // Buscar por ID ou nome
            const foundId = portfolioIds.find(id => {
                const idStr = String(id);
                const name = portfolios[id]?.toLowerCase() || '';
                const termLower = term.toLowerCase();
                return idStr.includes(term) || name.includes(termLower);
            });

            if (foundId) {
                setHighlightedPortfolio(foundId);
                // Scroll para o portfólio encontrado
                setTimeout(() => {
                    const element = document.querySelector(`[data-portfolio-id="${foundId}"]`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            } else {
                setHighlightedPortfolio(null);
            }
        }, 300); // Debounce de 300ms

        return () => clearTimeout(timeoutId);
    }, [searchTerm, portfolioIds, portfolios]);

    // Buscar célula específica (linha×coluna)
    useEffect(() => {
        const term = cellSearchTerm.trim();

        if (!term) {
            setHighlightedCell({ row: null, col: null });
            return;
        }

        const timeoutId = setTimeout(() => {
            // Aceita formatos: "456x467", "456×467", "456 467", "456,467"
            const match = term.match(/^(\d+)\s*[x×,\s]\s*(\d+)$/i);

            if (match) {
                const rowId = parseInt(match[1]);
                const colId = parseInt(match[2]);

                // Verificar se ambos os IDs existem
                if (portfolioIds.includes(rowId) && portfolioIds.includes(colId)) {
                    setHighlightedCell({ row: rowId, col: colId });

                    // Scroll para a célula encontrada (horizontal e vertical)
                    setTimeout(() => {
                        const scrollContainer = document.querySelector('.portfolio-matrix-scroll-container');
                        const rowElement = document.querySelector(`[data-portfolio-id="${rowId}"]`);

                        if (scrollContainer && rowElement) {
                            // Calcular posição da linha
                            const rowRect = rowElement.getBoundingClientRect();
                            const containerRect = scrollContainer.getBoundingClientRect();

                            // Calcular índice da coluna para scroll horizontal
                            const colIndex = portfolioIds.indexOf(colId);
                            const cellWidth = 32; // largura da célula
                            const headerWidth = 140; // largura do header de linha

                            // Scroll vertical (centralizar linha)
                            const currentScrollTop = scrollContainer.scrollTop;
                            const rowOffsetTop = rowElement.offsetTop;
                            const targetScrollTop = rowOffsetTop - (containerRect.height / 2) + (rowRect.height / 2);

                            // Scroll horizontal (centralizar coluna)
                            const targetScrollLeft = headerWidth + (colIndex * cellWidth) - (containerRect.width / 2) + (cellWidth / 2);

                            scrollContainer.scrollTo({
                                top: targetScrollTop,
                                left: targetScrollLeft,
                                behavior: 'smooth'
                            });
                        }
                    }, 100);
                } else {
                    setHighlightedCell({ row: null, col: null });
                }
            } else {
                setHighlightedCell({ row: null, col: null });
            }
        }, 300); // Debounce de 300ms

        return () => clearTimeout(timeoutId);
    }, [cellSearchTerm, portfolioIds]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            dialogClassName="portfolio-matrix-modal"
        >
            <Modal.Header className="bg-gradient-to-r from-slate-700 to-slate-600 text-white" closeButton>
                <Modal.Title>Configurar Portfólios Correlatos</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '75vh', overflow: 'hidden', padding: 0 }}>
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                        <p className="mt-2 text-gray-600">Carregando configuração...</p>
                    </div>
                ) : portfolioIds.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Nenhum portfólio disponível
                    </div>
                ) : (
                    <>
                        {/* Barra de ferramentas */}
                        <div className="portfolio-matrix-toolbar">
                            <div className="toolbar-search-group">
                                {/* <div className="toolbar-search">
                                <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar portfólio (ID ou nome)..."
                                    className="search-input"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => {
                                            setHighlightedPortfolio(null);
                                            setSearchTerm('');
                                        }}
                                        className="clear-search-button"
                                        title="Limpar busca"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div> */}
                                <div className="toolbar-search">
                                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        ref={cellSearchInputRef}
                                        type="text"
                                        value={cellSearchTerm}
                                        onChange={(e) => setCellSearchTerm(e.target.value)}
                                        placeholder="Buscar célula (ex: 456×467)..."
                                        className="search-input"
                                    />
                                    {cellSearchTerm && (
                                        <button
                                            onClick={() => {
                                                setHighlightedCell({ row: null, col: null });
                                                setCellSearchTerm('');
                                            }}
                                            className="clear-search-button"
                                            title="Limpar busca"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button onClick={handleClearAll} className="clear-all-button" title="Limpar todas as seleções">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Limpar Tudo
                            </button>
                        </div>

                        <div className="portfolio-matrix-wrapper">
                            <div className="portfolio-matrix-scroll-container">
                                <table className="portfolio-matrix-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-corner">Código Portifólio</th>
                                            {portfolioIds.map(id => (
                                                <ColumnHeader
                                                    key={id}
                                                    id={id}
                                                />
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {portfolioIds.map(rowId => (
                                            <tr key={rowId}>
                                                <RowHeader
                                                    id={rowId}
                                                    name={portfolios[rowId]}
                                                    isSearchHighlighted={highlightedPortfolio === rowId}
                                                />
                                                {portfolioIds.map(colId => (
                                                    <MatrixCell
                                                        key={colId}
                                                        rowId={rowId}
                                                        colId={colId}
                                                        isChecked={selectionMatrix[rowId]?.[colId]}
                                                        isDiagonal={rowId === colId}
                                                        onToggle={() => toggleSelection(rowId, colId)}
                                                        isHighlightedCell={highlightedCell.row === rowId && highlightedCell.col === colId}
                                                    />
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            {!isLoading && (
                <Modal.Footer className="bg-gray-50 border-t border-gray-200">
                    <Button
                        style={{
                            backgroundColor: '#dc3545',
                            borderColor: '#dc3545',
                            padding: '12px 50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            minWidth: '150px',
                            fontSize: '16px'
                        }}
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-whote border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancelar
                    </Button>
                    <Button
                        style={{
                            backgroundColor: '#003B6B',
                            borderColor: '#003B6B',
                            padding: '12px 50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            minWidth: '150px',
                            fontSize: '16px',
                        }}
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default PortfolioMatrixModal;
